import { getInputFormat, ReadFileSystem, ProgressCallback } from '@playcanvas/splat-transform';
import { AppBase, Asset, GSplatResource, Vec3 } from 'playcanvas';

import { Events } from './events';
import {
    loadGSplatData,
    validateGSplatData,
    dataTableToGSplatData
} from './io';
import { Splat } from './splat';
import { applyVolugraphMobileSplatCapIfConfigured } from './volugraph-mobile-splat-cap';

/** Match official SuperSplat loader; pipeline orientation is baked into canonical PLY/SOG at ingestion. */
const getOrientation = (filename: string): Vec3 => {
    try {
        const euler = (window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbedDefaultSplatEulerDeg?: number[] };
        }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbedDefaultSplatEulerDeg;
        if (
            Array.isArray(euler)
            && euler.length === 3
            && euler.every((n) => typeof n === 'number' && Number.isFinite(n))
        ) {
            return new Vec3(euler[0], euler[1], euler[2]);
        }
    } catch {
        /* ignore */
    }
    switch (getInputFormat(filename)) {
        case 'spz':
            return new Vec3(0, 0, 0);
        case 'lcc':
            return new Vec3(90, 0, 180);
        default:
            return new Vec3(0, 0, 180);
    }
};

// handles loading gsplat assets using splat-transform
class AssetLoader {
    app: AppBase;
    events: Events;

    constructor(app: AppBase, events: Events) {
        this.app = app;
        this.events = events;
    }

    async load(
        filename: string,
        fileSystem: ReadFileSystem,
        animationFrame?: boolean,
        skipReorder?: boolean
    ) {
        if (!animationFrame) {
            this.events.fire('startSpinner');
        }

        try {
            const reportDownloadProgress: ProgressCallback | undefined = animationFrame ? undefined : (loaded, total) => {
                const percent = total && total > 0
                    ? Math.min(100, Math.round((loaded / total) * 100))
                    : null;
                try {
                    window.dispatchEvent(new CustomEvent('volugraph-splat-download-progress', {
                        detail: { loaded, total, percent }
                    }));
                } catch {
                    /* ignore */
                }
            };

            let gsplatData = await loadGSplatData(
                filename,
                fileSystem,
                skipReorder || animationFrame,
                reportDownloadProgress
            );
            validateGSplatData(gsplatData);
            if (!animationFrame) {
                gsplatData = applyVolugraphMobileSplatCapIfConfigured(gsplatData);
            }

            const asset = new Asset(filename, 'gsplat', { url: `local-asset-${Date.now()}`, filename });
            this.app.assets.add(asset);
            asset.resource = new GSplatResource(this.app.graphicsDevice, gsplatData);
            try {
                window.dispatchEvent(new CustomEvent('volugraph-load-stage', {
                    detail: { stage: 'embed.gpu.complete', splats: gsplatData.numSplats }
                }));
            } catch {
                /* ignore */
            }

            return new Splat(asset, getOrientation(filename));
        } finally {
            if (!animationFrame) {
                this.events.fire('stopSpinner');
            }
        }
    }
}

export { AssetLoader };
